import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger, cn } from '@kognitos/lattice';

import { V1SecretState } from '@/generated/hooks/gringotts/models';

import type { TCredential } from '../types';

const STATUS: Partial<
  Record<V1SecretState, { className: string; messageKey: string }>
> = {
  [V1SecretState.SECRET_STATE_ACTIVE]: {
    className: 'bg-success',
    messageKey: 'credentials.status.active',
  },
  [V1SecretState.SECRET_STATE_PENDING_AUTHORIZATION]: {
    className: 'bg-warning',
    messageKey: 'credentials.status.pending',
  },
  [V1SecretState.SECRET_STATE_DEGRADED]: {
    className: 'bg-destructive',
    messageKey: 'credentials.status.degraded',
  },
};

/** Props for {@link CredentialStatusDot}. */
export interface ICredentialStatusDotProps {
  credential: TCredential;
}

/**
 * A colored dot for an OAuth credential's sign-in state, explained on hover.
 * Only OAuth credentials change state (key/value ones stay active whether or
 * not the key works), so others show nothing.
 *
 * @param props - Component props.
 * @returns The dot, or null.
 */
export function CredentialStatusDot({ credential }: ICredentialStatusDotProps) {
  const { t } = useTranslation('credentials');
  const status =
    credential.oauth_app_id && credential.state
      ? STATUS[credential.state]
      : undefined;
  if (!status) return null;
  const message = t(status.messageKey);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={message}
          tabIndex={0}
          className={cn('size-2 shrink-0 rounded-full', status.className)}
          data-testid="credential-status-dot"
        />
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
}
